import React, { useState, useRef, useEffect } from 'react';
import './DistrictSelector.css';

interface District {
  id: string;
  name: string;
  manager: string;
  username: string; // District manager username for API queries
  regional_manager: string;
  dealer_count: number;
  total_impressions?: number; // For district-specific totals
}

interface DistrictSelectorProps {
  districts: District[];
  selectedDistrict: District | null;
  onChange: (district: District | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

const DistrictSelector: React.FC<DistrictSelectorProps> = ({
  districts,
  selectedDistrict,
  onChange,
  placeholder = 'Select a district',
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter districts based on search term
  const filteredDistricts = districts.filter(district =>
    district.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    district.manager.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (district: District | null) => {
    onChange(district);
    setIsOpen(false);
    setSearchTerm('');
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm('');
      }
    }
  };

  return (
    <div className="district-selector" ref={dropdownRef}>
      <div 
        className={`district-selector-trigger ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`}
        onClick={toggleDropdown}
      >
        <span className="selected-value">
          {selectedDistrict ? selectedDistrict.name : placeholder}
        </span>
        <svg className="dropdown-arrow" width="12" height="8" viewBox="0 0 12 8" fill="none">
          <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      {isOpen && (
        <div className="district-selector-dropdown">
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Search districts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <div className="district-list">
            <div
              className={`district-option ${!selectedDistrict ? 'selected' : ''}`}
              onClick={() => handleSelect(null)}
            >
              <span className="district-name">All Districts</span>
              <span className="district-info">View all districts</span>
            </div>

            {filteredDistricts.map(district => (
              <div
                key={district.id}
                className={`district-option ${selectedDistrict?.id === district.id ? 'selected' : ''}`}
                onClick={() => handleSelect(district)}
              >
                <span className="district-name">{district.name}</span>
                <span className="district-info">
                  {district.dealer_count} dealers
                </span>
              </div>
            ))}

            {filteredDistricts.length === 0 && (
              <div className="no-results">
                No districts found matching "{searchTerm}"
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DistrictSelector;
